import moment from 'moment';

// The Date of Consultation label's values, formatted for display.
//
// TGCLOUD-1781. This used to be two copies of a `.map(format).join(', ')` — one
// in PedigreeChart's member-record build, one in ClinicalNotes' post-save
// refresh — and the JOIN is what the ticket is about. The label block renders one
// row per item in its array; handing it a single comma-joined string made every
// consultation ONE item, which then word-wrapped into an unreadable run of rows
// ("Consultation Date:" is already 18 characters against a ~15-character budget
// at the shipped font). Returning the array gives each consultation its own row,
// which is the rule Kathryn asked for.
//
// The formatting itself is deliberately unchanged. `appointmentDate` is a raw
// picker `Date`, so it carries a LOCAL-anchored day and `moment(value).format()`
// — which reads local components — is the correct reader. Switching to a
// UTC-component reader here would walk every consultation back a day west of
// Greenwich, which is the class of defect TGCLOUD-1631/1632/1633 were.
// `tokens` are moment tokens, straight from the user's Localisation preference,
// and are handed to moment verbatim like every other date on the chart.
//
// A formatter cannot be reached from here — this runs inside a member-record
// build, not a component — so the format string is a parameter rather than a
// private default, the same reason `fetusEventSubjectLabel` takes `fmtDate`.

export const DEFAULT_CONSULTATION_DATE_FORMAT = 'MM/DD/YYYY';

/**
 * Normalise a member's `consultationDate` to an array of formatted date
 * strings, one per consultation.
 *
 * The server sends an ISO array (`routes/pedigree.js` maps each appointment's
 * `appointmentDate`), but a member can also arrive carrying a value a previous
 * build already flattened, or nothing at all — so every shape is accepted and
 * an unusable one yields no rows rather than the string "Invalid date".
 *
 * @param {*} value    the raw `consultationDate` off a member record
 * @param {string} [tokens]  moment format tokens (the user's date preference)
 * @returns {string[]} formatted dates, order preserved; [] when there are none
 */
export const formatConsultationDates = (
  value,
  tokens = DEFAULT_CONSULTATION_DATE_FORMAT
) => {
  const fmt = tokens || DEFAULT_CONSULTATION_DATE_FORMAT;
  const raw = value == null ? [] : Array.isArray(value) ? value : [value];
  return raw
    .map((entry) => {
      if (entry == null || entry === '') return '';
      // An already-formatted string round-tripping through a refresh must not be
      // re-parsed: moment would read "12/03/2026" by its own heuristics and can
      // hand back a different day, or "Invalid date". Anything moment cannot
      // read as a date is passed through as the text it already is.
      const parsed = moment(entry, moment.ISO_8601, true);
      if (parsed.isValid()) return parsed.format(fmt);
      const loose = moment(entry);
      return loose.isValid() && entry instanceof Date
        ? loose.format(fmt)
        : String(entry).trim();
    })
    .filter(Boolean);
};
